import React, { useState, useEffect } from "react";
import {
  Drawer,
  Divider,
  Typography,
  TextField,
  Grid,
  Button,
  IconButton,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Box,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import Pagination from "@material-ui/lab/Pagination";
import { useStyles } from "./styles";
import RewardModal from "./RewardModal";

const RewardGroupDrawer = ({
  open,
  onClose,
  mode,
  group,
  accessGroups,
  onSave,
  isSaving,
}) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [emailDomain, setEmailDomain] = useState("");
  const [selectedAccessGroups, setSelectedAccessGroups] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTierIndex, setEditingTierIndex] = useState(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [accessGroupsTouched, setAccessGroupsTouched] = useState(false);
  const [tierPage, setTierPage] = useState(1);
  const TIERS_PER_PAGE = 10;

  useEffect(() => {
    if (open) {
      setNameTouched(false);
      setAccessGroupsTouched(false);
      setTierPage(1);
      if (mode === "edit" && group) {
        setName(group.name || "");
        setSignupCode(group.signupCode || "");
        setEmailDomain(group.emailDomain || "");
        setSelectedAccessGroups(group.accessGroupIDs || []);
        setTiers(group.rewards || []);
      } else {
        setName("");
        setSignupCode("");
        setEmailDomain("");
        setSelectedAccessGroups([]);
        setTiers([]);
      }
    }
  }, [open, mode, group]);

  const isFormValid = () => {
    return (
      name.trim().length > 0 &&
      selectedAccessGroups.length > 0 &&
      tiers.length > 0
    );
  };

  const handleSave = () => {
    const payload = {
      name: name.trim(),
      signupCode: signupCode.trim() || null,
      emailDomain: emailDomain.trim() || null,
      accessGroupIDs: selectedAccessGroups,
      rewards: tiers,
    };
    onSave(payload);
  };

  const handleAddTier = () => {
    setEditingTierIndex(null);
    setModalOpen(true);
  };

  const handleEditTier = (index) => {
    setEditingTierIndex(index);
    setModalOpen(true);
  };

  const handleDeleteTier = (index) => {
    setTiers((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      const maxPage = Math.ceil(updated.length / TIERS_PER_PAGE);
      if (tierPage > maxPage && maxPage > 0) {
        setTierPage(maxPage);
      }
      return updated;
    });
  };

  const handleTierSave = (tier) => {
    if (editingTierIndex !== null) {
      setTiers((prev) =>
        prev.map((t, i) => (i === editingTierIndex ? tier : t))
      );
    } else {
      setTiers((prev) => [...prev, tier]);
    }
    setModalOpen(false);
  };

  const getAccessGroupName = (id) => {
    const ag = accessGroups.find((g) => g.accessGroupID === id);
    return ag ? ag.name : id;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ className: classes.drawerPaper }}
        className="reward-group-drawer"
      >
        <div className={classes.drawerInner}>
          <div className={classes.drawerHeader}>
            <Typography variant="h6" color="primary" className="reward-group-drawer-title">
              {mode === "edit" ? group?.name || "Edit Group" : "Add Group"}
            </Typography>
          </div>

          <Divider />

          <div className={classes.drawerBody}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setNameTouched(true)}
                  error={nameTouched && name.trim().length === 0}
                  helperText={nameTouched && name.trim().length === 0 ? "Name is required" : ""}
                  inputProps={{ maxLength: 255 }}
                  className="reward-group-name-field"
                  data-id="reward-group-name"
                  data-testid="reward-group-name-input"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Signup Code"
                  variant="outlined"
                  size="small"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  inputProps={{ maxLength: 255 }}
                  className="reward-group-signup-code-field"
                  data-id="reward-group-signup-code"
                  data-testid="reward-group-signup-code-input"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Domain"
                  variant="outlined"
                  size="small"
                  value={emailDomain}
                  onChange={(e) => setEmailDomain(e.target.value)}
                  inputProps={{ maxLength: 255 }}
                  className="reward-group-email-domain-field"
                  data-id="reward-group-email-domain"
                  data-testid="reward-group-email-domain-input"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={accessGroupsTouched && selectedAccessGroups.length === 0}
                  className="reward-group-access-groups-field"
                  data-id="reward-group-access-groups"
                  data-testid="reward-group-access-groups-select"
                >
                  <InputLabel shrink={selectedAccessGroups.length > 0}>
                    Access Groups
                  </InputLabel>
                  <Select
                    multiple
                    value={selectedAccessGroups}
                    onChange={(e) => setSelectedAccessGroups(e.target.value)}
                    onClose={() => setAccessGroupsTouched(true)}
                    input={<OutlinedInput label="Access Groups" />}
                    renderValue={(selected) => (
                      <Box className={classes.chipsContainer}>
                        {selected.map((id) => (
                          <Chip
                            key={id}
                            label={getAccessGroupName(id)}
                            size="small"
                            data-id={id}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {accessGroups.map((ag) => (
                      <MenuItem key={ag.accessGroupID} value={ag.accessGroupID} data-id={ag.accessGroupID}>
                        {ag.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {accessGroupsTouched && selectedAccessGroups.length === 0 && (
                    <FormHelperText>At least one access group is required</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <div className={classes.tiersSection}>
              <div className={classes.tiersSectionHeader}>
                <Typography variant="subtitle2">Rewards</Typography>
                <Tooltip
                  title={tiers.length >= 100 ? "Maximum 100 rewards limit reached" : ""}
                  arrow
                >
                  <span>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddTier}
                      disabled={tiers.length >= 100}
                      className="reward-group-add-tier-btn"
                      data-id="reward-group-add-tier"
                      data-testid="reward-group-add-tier-button"
                    >
                      Add
                    </Button>
                  </span>
                </Tooltip>
              </div>

              {tiers
                .slice((tierPage - 1) * TIERS_PER_PAGE, tierPage * TIERS_PER_PAGE)
                .map((tier, pageIndex) => {
                  const index = (tierPage - 1) * TIERS_PER_PAGE + pageIndex;
                  return (
                    <div key={index} className={classes.rewardTierRow} data-id={`reward-tier-${index}`}>
                      <div className={classes.rewardTierInfo}>
                        <Typography variant="body2" className="reward-tier-info-text">
                          {tier.points} Points{" "}
                          {tier.type === "percent_off"
                            ? `${tier.amount}% off`
                            : `$${tier.amount} off`}
                        </Typography>
                      </div>
                      <div className={classes.rewardTierActions}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditTier(index)}
                          aria-label="Edit tier"
                          className="reward-tier-edit-btn"
                          data-id={`reward-tier-edit-${index}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTier(index)}
                          aria-label="Delete tier"
                          className="reward-tier-delete-btn"
                          data-id={`reward-tier-delete-${index}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}

              {tiers.length > TIERS_PER_PAGE && (
                <Pagination
                  count={Math.ceil(tiers.length / TIERS_PER_PAGE)}
                  page={tierPage}
                  onChange={(e, page) => setTierPage(page)}
                  size="small"
                  color="primary"
                  className={`${classes.tiersPagination} reward-tiers-pagination`}
                  data-testid="reward-tiers-pagination"
                />
              )}
            </div>
          </div>

          <Divider />

          <div className={classes.drawerFooter}>
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid() || isSaving}
              startIcon={
                isSaving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
              onClick={handleSave}
              className="reward-group-save-btn"
              data-id="reward-group-save"
              data-testid="reward-group-save-button"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              className="reward-group-close-btn"
              data-id="reward-group-close"
              data-testid="reward-group-close-button"
            >
              Close
            </Button>
          </div>
        </div>
      </Drawer>

      <RewardModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleTierSave}
        tier={editingTierIndex !== null ? tiers[editingTierIndex] : null}
      />
    </>
  );
};

export default RewardGroupDrawer;
